import * as React from 'react';
import { RatingChip } from '@/components/ui/rating-chip';
import type { ClientArea, NotAssessedArea } from '@/lib/api/vendor-intelligence';
import { cn } from '@/lib/utils/cn';
import { areaMeta, plural, weightWithNoEvidence } from './vi-logic';

/** A vendor across the matrix: the product and its fit figures, the server's. */
export interface AreaMatrixColumn {
  product_id: string;
  name: string;
  /** The vendor's name over the product's; omitted when it reads the same. */
  vendor?: string | null;
  /** "1", "2=" for a shared rank; null while unranked. */
  rank: string | null;
  /** The fit index, 0 to 100. */
  index: number;
  /** The share of the weight with evidence behind it, 0 to 100. */
  coverage: number;
}

export interface AreaCellRef {
  areaKey: string;
  productId: string;
}

/**
 * Fit by client area (T-0276; E24-design §4; ADR-026 decision 4): the
 * client's areas down, the vendors assessed in depth across, one rating
 * per cell rolled up by the server from the confirmed module ratings, and
 * two footer rows: the evidence index and the weight with no evidence (the
 * complement of the server's coverage). Areas whose requirements have no
 * module are listed under the table with their counts.
 *
 * Built once for the Shortlist step and the report's matrix page
 * (T-0283): the step passes `onOpenCell` so a cell opens the module
 * ratings behind it; the report passes a frozen fit's areas and none.
 * Every figure is the server's; nothing here computes one.
 */
export function AreaMatrix({
  areas,
  columns,
  notAssessed,
  selected,
  onOpenCell,
  label = 'Fit by client area',
  className,
}: {
  areas: readonly ClientArea[];
  columns: readonly AreaMatrixColumn[];
  notAssessed: readonly NotAssessedArea[];
  /** The cell whose panel is open. */
  selected?: AreaCellRef | null;
  /** Present: a cell is a button that opens its module ratings. */
  onOpenCell?: (cell: AreaCellRef) => void;
  /** The table's accessible name. */
  label?: string;
  className?: string;
}) {
  if (areas.length === 0) {
    return (
      <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
        No client area has a confirmed rating yet. Confirmed ratings roll up by the client's areas
        here.
      </p>
    );
  }
  if (columns.length === 0) {
    return (
      <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
        No vendor is shortlisted or an alternate, so there is nothing to compare by area.
      </p>
    );
  }
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="overflow-x-auto rounded-card border border-ink-100">
        <table className="w-full border-collapse text-body-sm" aria-label={label}>
          <thead>
            <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500 align-bottom">
              <th scope="col" className="min-w-[200px] px-3 py-2 font-medium">
                Client area
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Weight
              </th>
              {columns.map((c) => (
                <th
                  key={c.product_id}
                  scope="col"
                  className="whitespace-nowrap px-3 py-2 font-medium"
                >
                  {c.vendor ? <span className="block">{c.vendor}</span> : null}
                  <span className="block text-ink-900">{c.name}</span>
                  {c.rank ? (
                    <span className="block font-mono font-normal tabular-nums">#{c.rank}</span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => {
              const byProduct = new Map(a.products.map((p) => [p.product_id, p]));
              const meta = areaMeta(a);
              return (
                <tr key={a.key} className="border-b border-ink-100 align-top">
                  <th scope="row" className="px-3 py-2 text-left font-normal">
                    <span className="block text-ink-900">{a.name}</span>
                    {meta ? <span className="block text-meta text-ink-500">{meta}</span> : null}
                  </th>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-700">
                    {a.weight}
                  </td>
                  {columns.map((c) => {
                    const cell = byProduct.get(c.product_id);
                    const isSelected =
                      selected?.areaKey === a.key && selected?.productId === c.product_id;
                    return (
                      <td key={c.product_id} className="px-2 py-1.5">
                        {!cell ? (
                          <span className="px-1.5 text-meta text-ink-300">–</span>
                        ) : onOpenCell ? (
                          <button
                            type="button"
                            aria-label={`${c.name}, ${a.name}: ${cell.rating}`}
                            aria-pressed={isSelected}
                            title="Open the module ratings behind this"
                            onClick={() => onOpenCell({ areaKey: a.key, productId: c.product_id })}
                            className={cn(
                              // `relative` contains the chip's screen-reader text.
                              'relative inline-flex min-h-[30px] items-center rounded-chip px-1.5 hover:bg-paper-50',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                              isSelected && 'bg-paper-100',
                            )}
                          >
                            <RatingChip rating={cell.rating} state="confirmed" size="sm" />
                          </button>
                        ) : (
                          <RatingChip
                            rating={cell.rating}
                            state="confirmed"
                            size="sm"
                            className="mx-1.5"
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-b border-ink-100">
              <th scope="row" className="px-3 py-2 text-left font-semibold text-ink-900">
                Evidence index
              </th>
              <td className="px-3 py-2" />
              {columns.map((c) => (
                <td
                  key={c.product_id}
                  className="px-3 py-2 font-mono font-semibold tabular-nums text-ink-900"
                >
                  {c.index.toFixed(1)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" className="px-3 py-2 text-left font-normal text-ink-900">
                Weight with no evidence
              </th>
              <td className="px-3 py-2" />
              {columns.map((c) => (
                <td key={c.product_id} className="px-3 py-2 font-mono tabular-nums text-ink-700">
                  {weightWithNoEvidence(c)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {notAssessed.length > 0 ? (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-meta text-ink-500">
          <span>Not assessed by area, no module mapped:</span>
          <ul
            aria-label="Not assessed by area"
            className="m-0 flex list-none flex-wrap gap-x-3 p-0"
          >
            {notAssessed.map((n) => (
              <li key={n.key}>
                <span className="text-ink-700">{n.name}</span>{' '}
                {plural(n.requirements, 'requirement')}
                {n.critical > 0 ? ` (${n.critical} critical)` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
